<template>
  <div>
    <Title class="tw-mb-3">Import Contacts from CRM</Title>

    <!-- Latest Import Summary -->
    <div class="tw-flex tw-flex-col tw-gap-2 tw-mb-4">
      <template v-if="latest && Object.keys(latest).length > 0">
        <!-- <p>
          <span>Status</span>
          <Badge :status="statusColor">
            <span class="tw-font-bold">{{ latest.status }}</span>
          </Badge>
        </p>

        <div class="tw-flex tw-flex-col tw-text-sm tw-text-gray-500">
          <p>{{ isActive ? 'Started' : 'Finished' }} at:</p>
          <p>{{ displayDate }}</p>
        </div> -->

        
      </template>
      <template v-else>
        <p class="tw-text-sm tw-text-gray-500">Contact import has not been run yet</p>
      </template>
    </div>
    <!-- Filters -->
    <Subtitle size="3" class="tw-mb-2">Filters</Subtitle>
    <div class="tw-flex tw-items-end tw-gap-3 tw-flex-nowrap tw-mb-4">
      <div class="tw-flex tw-flex-col tw-gap-1">
        <label class="tw-text-xs tw-text-gray-500">Search</label>
        <input
          v-model="search"
          class="stk-input"
          placeholder="Search name…"
        >
      </div>

      <div class="tw-flex tw-flex-col tw-gap-1">
        <label class="tw-text-xs tw-text-gray-500">Contact Type</label>
        <Select
          v-model="filters.type"
          :options="typeOptions"
          placeholder="All types"
          clearable
          size="3_5"
        />
      </div>

      <div class="tw-flex tw-flex-col tw-gap-1">
        <label class="tw-text-xs tw-text-gray-500">Created After</label>
        <div class="tw-flex tw-gap-2">
          <input
            v-model="filters.createdFrom"
            class="stk-input"
            type="date"
          >
        </div>
      </div>
      <Button variant="" @click="clearFilters">Clear Filters</Button>

    </div>

    <!-- High-level Counts (as badges) -->
    <div class="tw-flex tw-gap-2 tw-items-center tw-flex-wrap tw-mb-4">
      <div class="tw-flex tw-items-center tw-gap-1">
        <span class="tw-text-xs tw-text-gray-500">Created</span>
        <Badge status="success"><span class="tw-font-medium">{{ created }}</span></Badge>
      </div>
      <div class="tw-flex tw-items-center tw-gap-1">
        <span class="tw-text-xs tw-text-gray-500">Updated</span>
        <Badge status="info"><span class="tw-font-medium">{{ updated }}</span></Badge>
      </div>
      <div class="tw-flex tw-items-center tw-gap-1">
        <span class="tw-text-xs tw-text-gray-500">Skipped</span>
        <Badge status="warning"><span class="tw-font-medium">{{ skipped }}</span></Badge>
      </div>
      <div class="tw-flex tw-items-center tw-gap-1">
        <span class="tw-text-xs tw-text-gray-500">Failed</span>
        <Badge status="danger"><span class="tw-font-medium">{{ failed }}</span></Badge>
      </div>
      <div class="tw-flex tw-items-center tw-gap-1">
        <span class="tw-text-xs tw-text-gray-500">Total</span>
        <Badge><span class="tw-font-medium">{{ total }}</span></Badge>
      </div>
    </div>

    <div class="tw-flex tw-justify-end tw-mb-4">
      <Buttons size="3_5">
        <Button
          variant="primary"
          :disabled="!canImport"
          icon-before="fas fa-download"
          @click="importSelected"
        >
          Import Selected<span v-if="selectedCount"> ({{ selectedCount }})</span>
        </Button>
      </Buttons>
    </div>

    <!-- Table -->
    <table class="component">
      <thead class="head">
        <tr>
          <th class="col-head is-select">
            <label class="col-head-button">
              <input
                type="checkbox"
                :checked="areAllVisibleSelected"
                @change="toggleSelectAll"
              >
            </label>
          </th>
          <th class="col-head"><span class="col-head-button">Name</span></th>
          <th class="col-head"><span class="col-head-button">Contact Type</span></th>
          <th class="col-head"><span class="col-head-button">Import Status</span></th>
          <th class="col-head"><span class="col-head-button">Created</span></th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="row in filteredRows"
          :key="row.id"
          class="row"
        >
          <td class="cell is-select">
            <input
              type="checkbox"
              :checked="isSelected(row.id)"
              @change="toggleRow(row.id)"
            >
          </td>
          <td class="cell">{{ row.name }}</td>
          <td class="cell">
            <Badge :status="typeBadge(row.type)">{{ row.type }}</Badge>
          </td>
          <td class="cell">
            <div class="tw-flex tw-flex-col tw-gap-0_5">
              <Badge :status="statusBadge(row.id)">{{ statusLabel(row.id) }}</Badge>
              <!-- <small class="tw-text-xs tw-text-gray-500" v-if="statusLastAttempted(row.id)">{{ statusLastAttempted(row.id) }}</small> -->
            </div>
          </td>
          <td class="cell">{{ formatDate(row.created) }}</td>
        </tr>
      </tbody>
    </table>
  </div>
  
</template>

<script>
import { formatDate as sharedFormatDate } from '@frontend/shared/Util.js';
import { Badge, Button, Buttons, Select, Subtitle, Title } from '@stakeui';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore.js';
import { fetchWrapper } from '@frontend/shared/fetch-wrapper.js';

export default {
  name: 'CRMContactsTable',
  components: { Badge, Button, Buttons, Select, Subtitle, Title },
  props: {
    app: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      rows: [],
      contactsRaw: [],
      selectedIds: [],
      search: '',
      filters: {
        type: null,
        createdFrom: '',
      },
      latest: null,
      refreshInterval: null,
      statusById: {},
      selectAllMode: false,
    };
  },
  computed: {
    advisorStore() {
      return useAdvisorStore();
    },
    selectedCrm() {
      return (this.app && this.app.name) ? this.app.name : null;
    },
    typeOptions() {
      const unique = [...new Set(this.rows.map(r => r.type))];
      return unique.map(v => ({ value: v, label: v }));
    },
    filteredRows() {
      let data = this.rows;
      if (this.search) {
        const q = this.search.toLowerCase();
        data = data.filter(r => r.name.toLowerCase().includes(q));
      }
      if (this.filters.type) {
        data = data.filter(r => r.type === this.filters.type);
      }
      if (this.filters.createdFrom) {
        const from = new Date(this.filters.createdFrom);
        data = data.filter(r => {
          const d = r.created ? new Date(r.created) : null;
          if (!d) return false;
          if (from && d < from) return false;
          return true;
        });
      }
      return data;
    },
    areAllVisibleSelected() {
      if (this.filteredRows.length === 0) return false;
      const visibleIds = this.filteredRows.map(r => r.id);
      return visibleIds.every(id => this.selectedIds.includes(id));
    },
    selectedCount() {
      return this.selectedIds.length;
    },
    canImport() {
      return this.selectedIds.length > 0 && !!this.selectedCrm;
    },
    isActive() {
      if (!this.latest) return false;
      return ['pending', 'started'].includes(this.latest.status);
    },
    statusColor() {
      if (!this.latest) return '';
      const status = this.latest.status;
      if (status === 'complete') return 'success';
      if (status === 'failed') return 'danger';
      if (['pending', 'started'].includes(status)) return 'info';
      return '';
    },
    created() {
      if (!this.latest) return 0;
      return this.latest.meta?.created || 0;
    },
    updated() {
      if (!this.latest) return 0;
      return this.latest.meta?.updated || 0;
    },
    skipped() {
      if (!this.latest) return 0;
      const skipped = this.latest.meta?.skipped;
      if (!skipped || Object.keys(skipped).length === 0) return 0;
      return Object.values(skipped).map(ids => ids.length).reduce((a, b) => a + b, 0);
    },
    failed() {
      if (!this.latest) return 0;
      // Use meta.failed if provided; otherwise default to 0
      const f = this.latest.meta?.failed;
      if (typeof f === 'number') return f;
      if (Array.isArray(f)) return f.length;
      return 0;
    },
    total() {
      return this.created + this.updated + this.skipped + this.failed;
    },
    date() {
      if (!this.latest) return null;
      const effective = this.isActive ? this.latest.started : this.latest.finished;
      if (!effective) return null;
      return new Date(effective);
    },
    displayDate() {
      if (!this.date) return null;
      return sharedFormatDate(this.date);
    },
  },
  mounted() {
    this.fetchContacts();
    this.getLatestImport();
  },
  watch: {
    // React to filter inputs changing; keep selection in sync with visible rows
    'filters.type'() { this.syncSelectionWithFilters(); },
    'filters.createdFrom'() { this.syncSelectionWithFilters(); },
    search() { this.syncSelectionWithFilters(); },
  },
  beforeDestroy() {
    this.stopRefresh();
  },
  methods: {
    formatDate(date) {
      return sharedFormatDate(new Date(date));
    },
    syncSelectionWithFilters() {
      const visibleIds = this.filteredRows.map(r => r.id);
      if (this.selectAllMode) {
        // When in select-all mode, always select all visible rows
        this.selectedIds = [...visibleIds];
      } else {
        // Otherwise, constrain selection to visible subset, preserving manual selections
        this.selectedIds = this.selectedIds.filter(id => visibleIds.includes(id));
      }
    },
    typeBadge(type) {
      if (type === 'Client') return 'success';
      if (type === 'Prospect') return 'info';
      return '';
    },
    isSelected(id) {
      return this.selectedIds.includes(id);
    },
    toggleRow(id) {
      // Toggling individual rows exits select-all mode
      this.selectAllMode = false;
      if (this.isSelected(id)) {
        this.selectedIds = this.selectedIds.filter(x => x !== id);
      } else {
        this.selectedIds = [...this.selectedIds, id];
      }
    },
    toggleSelectAll(e) {
      const checked = e.target.checked;
      const visibleIds = this.filteredRows.map(r => r.id);
      if (checked) {
        // Enter select-all mode scoped to visible rows
        this.selectAllMode = true;
        const combined = new Set([...visibleIds]);
        this.selectedIds = Array.from(combined);
      } else {
        // Exiting select-all mode keeps only selections outside current visible set
        this.selectAllMode = false;
        this.selectedIds = this.selectedIds.filter(id => !visibleIds.includes(id));
      }
    },
    clearFilters() {
      this.search = '';
      this.filters.type = null;
      this.filters.createdFrom = '';
    },
    async fetchStatusesForVisible() {
      try {
        const crm = (this.selectedCrm || '').trim();
        if (!crm) return;
        const ids = this.filteredRows.map(r => r.id).filter(Boolean).map(String);
        if (ids.length === 0) return;
        const unique = Array.from(new Set(ids));
        const url = `/api/v1/crm/general/import/contacts/statuses/?crm=${encodeURIComponent(crm)}&ids=${encodeURIComponent(unique.join(','))}`;
        const res = await fetchWrapper(url, { headers: this.advisorStore.headers });
        const data = await res.json();
        this.statusById = data.statuses || {};
      } catch (e) {
        console.error('Failed to fetch per-contact statuses', e);
      }
    },
    statusBadge(id) {
      const s = this.statusById?.[String(id)]?.status;
      if (s === 'imported') return 'success';
      if (s === 'skipped') return 'warning';
      if (s === 'pending') return 'info';
      return '';
    },
    statusLabel(id) {
      const s = this.statusById?.[String(id)]?.status || 'not_imported';
      if (s === 'imported') return 'Imported';
      if (s === 'skipped') return 'Skipped';
      if (s === 'pending') return 'Pending';
      return 'Not Imported';
    },
    statusLastAttempted(id) {
      const ts = this.statusById?.[String(id)]?.last_attempted;
      if (!ts) return null;
      try { return sharedFormatDate(new Date(ts)); } catch (_e) { return null; }
    },
    async importSelected() {
      try {
        const crm = (this.selectedCrm || '').trim();
        if (!crm) return;
        const url = `/api/v1/crm/general/import/contacts/selected/?crm=${encodeURIComponent(crm)}`;
        const res = await fetchWrapper(url, {
          method: 'POST',
          headers: {
            ...this.advisorStore.headers,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ contactIds: this.selectedIds }),
        });
        const data = await res.json();
        if (data && data.task_id) {
          alert('Import started. You can monitor status in the Import Contacts widget.');
          // Also begin polling in this component
          this.getLatestImport();
          // Refresh per-contact statuses to reflect pending state
          this.fetchStatusesForVisible();
        } else if (data && data.counts) {
          // Fallback if server returns immediate counts
          alert(`Imported: ${data?.counts?.created || 0} created, ${data?.counts?.updated || 0} updated, ${data?.counts?.skipped || 0} skipped`);
        } else {
          alert('Import request submitted.');
        }
      } catch (e) {
        console.error('Failed to import selected contacts', e);
        alert('Failed to import selected contacts');
      }
    },
    async getLatestImport() {
      try {
        const crm = (this.selectedCrm || '').trim();
        if (!crm) return;
        const url = `/api/v1/crm/general/import/contacts/?crm=${encodeURIComponent(crm)}`;
        const res = await fetchWrapper(url, {
          method: 'GET',
          headers: this.advisorStore.headers,
        });
        let data = {};
        try {
          data = await res.json();
        } catch (_e) {
          data = {};
        }
        this.latest = data && typeof data === 'object' ? data : {};
        if (this.isActive) this.startRefresh(); else this.stopRefresh();
        // Update per-contact statuses when latest import changes
        this.fetchStatusesForVisible();
      } catch (e) {
        console.error('Failed to fetch latest import status', e);
      }
    },
    startRefresh() {
      if (this.refreshInterval) return;
      this.refreshInterval = setInterval(() => {
        this.getLatestImport();
      }, 1000);
    },
    stopRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
    },
    async fetchContacts() {
      try {
        const crm = (this.selectedCrm || '').toLowerCase();
        if (!crm) return;
        if (crm === 'redtail') {
          await this.fetchAllRedtailContacts();
          return;
        }
        const url = `/api/v1/crm/${crm}/contacts/`;
        const res = await fetchWrapper(url, {
          headers: this.advisorStore.headers,
        });
        const data = await res.json();
        const contacts = data.contacts || [];
        this.contactsRaw = contacts;
        this.rows = this.mapContacts(contacts, crm);
      // Select all by default after loading
      this.selectedIds = this.rows.map(r => r.id).filter(id => id !== undefined && id !== null);
      this.selectAllMode = true;
        // After rows are loaded, fetch statuses for them
        this.fetchStatusesForVisible();
      } catch (e) {
        console.error('Failed to fetch CRM contacts', e);
        this.contactsRaw = [];
        this.rows = [];
      }
    },
    async fetchAllRedtailContacts() {
      const baseBackendUrl = '/api/v1/crm/redtail/contacts/';
      // First call: use backend endpoint to get page 1 and meta
      const firstRes = await fetchWrapper(baseBackendUrl, {
        headers: this.advisorStore.headers,
      });
      const firstData = await firstRes.json();
      const firstContacts = firstData.contacts || [];
      const meta = firstData.meta || {};
      const totalPages = meta.total_pages || meta.totalPages || 1;

      let all = [...firstContacts];

      if (totalPages > 1) {
        // Fetch remaining pages via OAuth proxy to honor auth and include headers
        const params = new URLSearchParams({
          pagesize: '200',
          shared_phones: 'true',
          shared_addresses: 'true',
        });

        for (let page = 2; page <= totalPages; page += 1) {
          params.set('page', String(page));
          const proxyUrl = `/api/v1/oauth/proxy/redtail/contacts/search?${params.toString()}`;
          const res = await fetchWrapper(proxyUrl, {
            headers: {
              ...this.advisorStore.headers,
              include: 'phones,emails,addresses',
            },
          });
          const data = await res.json();
          const contacts = data.contacts || [];
          all = all.concat(contacts);
        }
      }

      this.contactsRaw = all;
      this.rows = this.mapContacts(all, 'redtail');
      // Select all by default after loading
      this.selectedIds = this.rows.map(r => r.id).filter(id => id !== undefined && id !== null);
      this.selectAllMode = true;
    },
    mapContacts(contacts, crm) {
      if (!Array.isArray(contacts)) return [];
      return contacts.map(c => this.mapContact(c, crm));
    },
    mapContact(contact, crm) {
      const lower = crm;
      if (lower === 'redtail') {
        return {
          id: contact.id || contact.contact_id || contact.ID || contact.Id || contact.Id__c,
          name: contact.full_name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.name || 'Unknown',
          type: contact.record_type || contact.type || '',
          created: contact.created_at || contact.created || contact.date_created || contact.CreatedDate || '',
        };
      }
      if (lower === 'wealthbox') {
        return {
          id: contact.id || contact.ID || contact.Id,
          name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.name || 'Unknown',
          type: contact.type || '',
          created: contact.created_at || contact.created || '',
        };
      }
      if (lower === 'salesforce') {
        return {
          id: contact.Id || contact.id,
          name: contact.Name || contact.name || 'Unknown',
          type: contact.Type || contact.RecordType?.Name || '',
          created: contact.CreatedDate || contact.created || '',
        };
      }
      return {
        id: contact.id || contact.Id || `${Math.random()}`,
        name: contact.name || 'Unknown',
        type: contact.type || '',
        created: contact.created || contact.created_at || '',
      };
    },
  },
};
</script>

<style scoped>
.component {
  border-bottom-style: solid;
  border-bottom-width: 1px;
  border-bottom-color: var(--stk-text-85);
  width: 100%;
}

.head {
  background-color: hsl(var(--stk-text-h) var(--stk-text-s) 98%);
  border-top: 1px solid var(--stk-text-85);
}

.col-head {
  color: var(--stk-text-60);
  font-weight: 500;
  font-size: 0.75em;
  padding: 0;
}

.col-head .col-head-button {
  align-items: center;
  display: flex;
  padding: 0.25rem 0.75rem;
}

/* Ensure header checkbox is centered vertically */
.head .col-head.is-select {
  vertical-align: middle;
}
.head .col-head.is-select .col-head-button {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 2.25rem;
}
.head .col-head.is-select input[type="checkbox"] {
  align-self: center;
}

.row {
  border-top-style: solid;
  border-top-width: 1px;
  border-top-color: var(--stk-text-90);
}

.cell {
  vertical-align: middle;
  padding: 0.75rem;
}

.is-select {
  width: 3rem;
}
</style>


